import InfoTooltip from 'Components/InfoTooltip'
import LabelTableCell from 'Components/LabelTableCell'
import StyledTableRow from 'Components/StyledTableRow'
import TableCellContentWithTooltip from 'Components/TableCellContentWithTooltip'
import TranslatedField from 'Components/TranslatedField'
import type { ParameterGroup } from 'Services/ProductManagement/Features/Parameter/types'
import type { VATDefinition } from 'Services/ProductManagement/Features/VATDefinition/types'
import { DEFAULT_VAT_DEFINITIONS, WALTTI_ENABLED_FEATURES } from 'Util/constants'
import {
  datetimeFormatter,
  getValueFromOptions,
  getValuesFromOptions,
  isoDurationToHumanReadableFormat,
  showValueOrHyphen
} from 'Util/formatters'
import getTranslation from 'Util/getTranslation'
import {
  CircularProgress,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  Typography
} from '@mui/material'
import Paper from '@mui/material/Paper'
import { t } from 'ttag'
import { AllowedActivationMethods, type Product, ProductUsages } from '../../productSlice'
import { AllowedActivationMethodsInfoTooltip } from '../AllowedActivationMethodsInfoTooltip'

const StyledTableCell = styled(LabelTableCell)`
  width: 40%;
`

interface ProductDetailsProps {
  product: Product
  productGroupParameterGroup: ParameterGroup | null
  isProductGroupParameterGroupLoading: boolean
  customerGroupParameterGroup: ParameterGroup | null
  isCustomerGroupParameterGroupLoading: boolean
  allowedTokensParameterGroup: ParameterGroup | null
  isAllowedTokensParameterGroupLoading: boolean
  vatDefinition?: VATDefinition | null
  isVatDefinitionsLoading?: boolean
}

function ProductDetails({
  product,
  productGroupParameterGroup,
  isProductGroupParameterGroupLoading,
  customerGroupParameterGroup,
  isCustomerGroupParameterGroupLoading,
  allowedTokensParameterGroup,
  isAllowedTokensParameterGroupLoading,
  vatDefinition,
  isVatDefinitionsLoading
}: Readonly<ProductDetailsProps>) {
  const productGroupOptions =
    productGroupParameterGroup?.parameters.map(({ translations, parameter_id }) => ({
      label: getTranslation(translations, 'fi').value ?? '',
      value: parameter_id
    })) ?? []

  const customerGroupOptions =
    customerGroupParameterGroup?.parameters.map(({ translations, parameter_id }) => ({
      label: getTranslation(translations, 'fi').value ?? '',
      value: parameter_id
    })) ?? []

  const allowedTokenOptions =
    allowedTokensParameterGroup?.parameters.map(({ translations, parameter_id }) => ({
      label: getTranslation(translations, 'fi').value ?? '',
      value: parameter_id
    })) ?? []

  const vatDefinitionOptions =
    vatDefinition?.vat_percentages?.map(({ vat_code, vat_percent }) => ({
      label: `${vat_code} (${vat_percent} %)`,
      value: vat_code
    })) ?? DEFAULT_VAT_DEFINITIONS

  const allowedActivationMethodOptions = [
    {
      label: t`Purchase`,
      value: AllowedActivationMethods.PURCHASE
    },
    {
      label: t`First use`,
      value: AllowedActivationMethods.FIRST_USE
    },
    {
      label: t`Timed`,
      value: AllowedActivationMethods.TIMED
    }
  ]

  const sharedUsageOptions = [
    {
      label: t`Single user`,
      value: ProductUsages.SINGLE_USER
    },
    {
      label: t`Concurrent users`,
      value: ProductUsages.CONCURRENT_USERS
    }
  ]

  const booleanOptions = [
    {
      label: t`Yes`,
      value: true
    },
    {
      label: t`No`,
      value: false
    }
  ]

  return (
    <TableContainer component={Paper}>
      <Table sx={{ td: { border: 'none' } }} aria-label={t`Product details table`}>
        <TableBody>
          <StyledTableRow>
            <StyledTableCell>{t`Product code`}</StyledTableCell>
            <TableCell>{showValueOrHyphen(product.product_code)}</TableCell>
          </StyledTableRow>
          <StyledTableRow>
            <StyledTableCell>
              <TableCellContentWithTooltip
                tooltip={
                  <InfoTooltip
                    title={
                      <Typography variant="body2">
                        {t`Product type affects the sales rules used for the product. Available product
                  types are managed in the product parameters section.`}
                      </Typography>
                    }
                  />
                }
              >
                {t`Product type`}
              </TableCellContentWithTooltip>
            </StyledTableCell>
            <TableCell>
              {isProductGroupParameterGroupLoading ? (
                <CircularProgress size={18} />
              ) : (
                getValueFromOptions(product.product_group, productGroupOptions)
              )}
            </TableCell>
          </StyledTableRow>
          <StyledTableRow>
            <StyledTableCell>{t`Product name`}</StyledTableCell>
            <TableCell>
              <TranslatedField
                key="product-name"
                field="name"
                translations={product.translations}
              />
            </TableCell>
          </StyledTableRow>
          <StyledTableRow>
            <StyledTableCell>{t`Short name`}</StyledTableCell>
            <TableCell>
              <TranslatedField
                key="product-short-name"
                field="short_name"
                translations={product.translations}
              />
            </TableCell>
          </StyledTableRow>
          <StyledTableRow>
            <StyledTableCell>{t`Description`}</StyledTableCell>
            <TableCell>
              <TranslatedField
                key="product-description"
                field="description"
                translations={product.translations}
              />
            </TableCell>
          </StyledTableRow>
          <StyledTableRow>
            <StyledTableCell>{t`Additional name`}</StyledTableCell>
            <TableCell>
              <TranslatedField
                key="product-additional-name"
                field="additional_name"
                translations={product.translations}
              />
            </TableCell>
          </StyledTableRow>
          <StyledTableRow>
            <StyledTableCell>{t`Validity length`}</StyledTableCell>
            <TableCell>
              {isoDurationToHumanReadableFormat(product.usage_validity_duration)}
            </TableCell>
          </StyledTableRow>
          <StyledTableRow>
            <StyledTableCell>
              <TableCellContentWithTooltip tooltip={<AllowedActivationMethodsInfoTooltip />}>
                {t`Allowed activation method`}
              </TableCellContentWithTooltip>
            </StyledTableCell>
            <TableCell>
              {getValuesFromOptions(product.usage_trigger_event, allowedActivationMethodOptions)}
            </TableCell>
          </StyledTableRow>
          <StyledTableRow>
            <StyledTableCell>{t`Purchase in advance max`}</StyledTableCell>
            <TableCell>
              {isoDurationToHumanReadableFormat(product.max_purchase_advance_duration)}
            </TableCell>
          </StyledTableRow>
          <StyledTableRow>
            <StyledTableCell>{t`Activation window duration`}</StyledTableCell>
            <TableCell>
              {isoDurationToHumanReadableFormat(product.activation_window.duration)}
            </TableCell>
          </StyledTableRow>
          <StyledTableRow>
            <StyledTableCell>{t`Customer group`}</StyledTableCell>
            <TableCell>
              {isCustomerGroupParameterGroupLoading ? (
                <CircularProgress size={18} />
              ) : (
                getValueFromOptions(product.customer_group, customerGroupOptions, '-')
              )}
            </TableCell>
          </StyledTableRow>
          <StyledTableRow>
            <StyledTableCell>{t`Allowed tokens`}</StyledTableCell>
            <TableCell>
              {isAllowedTokensParameterGroupLoading ? (
                <CircularProgress size={18} />
              ) : (
                getValuesFromOptions(product.allowed_tokens, allowedTokenOptions)
              )}
            </TableCell>
          </StyledTableRow>
          <StyledTableRow>
            <StyledTableCell>{t`Value added tax`}</StyledTableCell>
            <TableCell>
              {isVatDefinitionsLoading ? (
                <CircularProgress size={18} />
              ) : (
                getValueFromOptions(product.vat_code, vatDefinitionOptions)
              )}
            </TableCell>
          </StyledTableRow>
          <StyledTableRow>
            <StyledTableCell>
              <TableCellContentWithTooltip
                tooltip={
                  <InfoTooltip
                    title={
                      <Typography variant="body2">
                        {t`The value determines whether the product can be used only by the travel account holder or also by fellow passengers.`}
                      </Typography>
                    }
                  />
                }
              >
                {t`Shared usage`}
              </TableCellContentWithTooltip>
            </StyledTableCell>
            <TableCell>{getValueFromOptions(product.shared_usage, sharedUsageOptions)}</TableCell>
          </StyledTableRow>
          <StyledTableRow>
            <StyledTableCell>{t`Strong identification required`}</StyledTableCell>
            <TableCell>
              {getValueFromOptions(product.strong_identification_required, booleanOptions)}
            </TableCell>
          </StyledTableRow>
          {WALTTI_ENABLED_FEATURES && (
            <StyledTableRow>
              <StyledTableCell>{t`Print zero price`}</StyledTableCell>
              <TableCell>{getValueFromOptions(product.print_zero_price, booleanOptions)}</TableCell>
            </StyledTableRow>
          )}
          <StyledTableRow>
            <StyledTableCell>{t`Authority`}</StyledTableCell>
            <TableCell>{showValueOrHyphen(product.product_owner)}</TableCell>
          </StyledTableRow>
          <StyledTableRow>
            <StyledTableCell>{t`Created`}</StyledTableCell>
            <TableCell>{datetimeFormatter(new Date(product.created_timestamp))}</TableCell>
          </StyledTableRow>
          <StyledTableRow>
            <StyledTableCell>{t`Modified`}</StyledTableCell>
            <TableCell>{datetimeFormatter(new Date(product.modified_timestamp))}</TableCell>
          </StyledTableRow>
          <StyledTableRow>
            <StyledTableCell>{t`Product ID`}</StyledTableCell>
            <TableCell>{showValueOrHyphen(product.id)}</TableCell>
          </StyledTableRow>
        </TableBody>
      </Table>
    </TableContainer>
  )
}

export default ProductDetails
